import React from 'react';
import { Link } from 'react-router-dom';

export default function ReleasePolicy() {
  return (
    <div className="policy-page" data-testid="release-policy-page">
      <div className="policy-header">
        <h1>Release Policy</h1>
        <p className="policy-updated">Last updated: March 2025</p>
      </div>

      <div className="policy-content">
        <section className="policy-section">
          <h2>1. Overview</h2>
          <p>
            This Release Policy governs the process, terms, and conditions for all music
            releases distributed through CloudfiMusic, operated by Xuân Diệu Media Co., Ltd.
            By submitting music for release, artists acknowledge and agree to the terms
            outlined in this document.
          </p>
        </section>

        <section className="policy-section">
          <h2>2. Submission & Review Process</h2>
          <ul>
            <li>
              All submissions must be sent to <a href="mailto:info@cloudfimusic.com">info@cloudfimusic.com</a> with
              the subject line “Release Submission — [Artist Name] — [Track/EP Title].”
            </li>
            <li>
              Submissions must include: final mastered audio files (WAV, 16-bit/44.1kHz minimum),
              track metadata (title, BPM, key, credits), and artwork (3000x3000px, JPEG/PNG).
            </li>
            <li>
              CloudfiMusic’s A&R team will review submissions within <strong>14 business days</strong>.
              The label reserves the sole right to accept or decline any submission without
              obligation to provide detailed feedback.
            </li>
            <li>
              Accepted submissions will proceed to the release pipeline. Declined submissions
              may be resubmitted after substantial revision, subject to a 30-day waiting period.
            </li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>3. Release Schedule & Timeline</h2>
          <ul>
            <li>
              CloudfiMusic operates a <strong>label-managed release schedule</strong>. The label
              determines release dates based on strategic planning, catalog spacing, and
              marketing considerations.
            </li>
            <li>
              Artists will be notified of their confirmed release date at least <strong>21 days</strong> prior
              to distribution. Requests to change release dates must be submitted at least
              14 days in advance and are subject to approval.
            </li>
            <li>
              CloudfiMusic reserves the right to delay or reschedule releases for operational,
              legal, or strategic reasons. Artists will be informed promptly of any schedule changes.
            </li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>4. Distribution</h2>
          <p>
            CloudfiMusic distributes music through its authorized distribution partners to
            major digital platforms including, but not limited to:
          </p>
          <ul>
            <li>Spotify, Apple Music, Amazon Music, YouTube Music</li>
            <li>Tidal, Deezer, Pandora, SoundCloud</li>
            <li>TikTok, Instagram/Facebook, Snapchat</li>
            <li>Additional platforms as determined by the label</li>
          </ul>
          <p>
            CloudfiMusic holds <strong>exclusive distribution rights</strong> for all releases
            under the label. Artists may not independently distribute the same recordings
            to any platform during the agreement term without prior written consent.
          </p>
        </section>

        <section className="policy-section">
          <h2>5. Revenue & Royalties</h2>
          <ul>
            <li>
              Streaming and download revenue will be split between the artist and CloudfiMusic
              as specified in the individual artist agreement. In the absence of a specific
              agreement, the default revenue share is <strong>70% to CloudfiMusic, 30% to the artist</strong> for
              label-funded releases, and <strong>50/50</strong> for artist-funded productions.
            </li>
            <li>
              Royalty statements will be provided on a <strong>quarterly basis</strong> (every 3 months).
              Payments will be processed within 45 days following the end of each quarter,
              subject to a minimum payout threshold of <strong>$50 USD</strong>.
            </li>
            <li>
              CloudfiMusic reserves the right to recoup any advances, production costs,
              marketing expenses, or promotional investments from the artist’s royalty share
              before distributing earnings.
            </li>
            <li>
              Revenue from playlist placements, compilation appearances, and label-curated
              content shall be allocated at CloudfiMusic’s discretion, with the artist receiving
              their proportional share as defined in their agreement.
            </li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>6. Exclusivity & Lock-In Period</h2>
          <ul>
            <li>
              All releases distributed through CloudfiMusic are subject to an
              <strong> exclusivity period of 12 months</strong> from the release date, during which
              the tracks may not be released, re-recorded, or distributed through any other
              label or distributor.
            </li>
            <li>
              After the exclusivity period, the agreement automatically renews for successive
              12-month terms unless either party provides <strong>60 days written notice</strong> of
              non-renewal.
            </li>
            <li>
              During the exclusivity period, CloudfiMusic retains full administrative and
              distribution control over the released content.
            </li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>7. Artwork & Visual Assets</h2>
          <ul>
            <li>
              Release artwork may be provided by the artist or created by CloudfiMusic’s
              design team. In either case, CloudfiMusic retains the right to use, modify,
              or replace artwork for promotional and distribution purposes.
            </li>
            <li>
              Artist-provided artwork must be original or properly licensed. The artist
              is responsible for any claims arising from unauthorized use of visual elements.
            </li>
            <li>
              CloudfiMusic retains ownership of all artwork, visual assets, and promotional
              materials created by the label’s team in connection with a release.
            </li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>8. Marketing & Promotion</h2>
          <p>
            CloudfiMusic will make reasonable efforts to promote releases through its channels,
            including but not limited to:
          </p>
          <ul>
            <li>Official CloudfiMusic playlists and social media accounts</li>
            <li>Editorial playlist pitching to streaming platforms</li>
            <li>Blog and media outreach</li>
            <li>Cross-promotion with other CloudfiMusic artists</li>
          </ul>
          <p>
            The scope and intensity of promotional efforts are at CloudfiMusic’s discretion
            and may vary based on strategic priorities, release quality, and available resources.
            CloudfiMusic does not guarantee any specific level of streams, exposure, or revenue.
          </p>
        </section>

        <section className="policy-section">
          <h2>9. Takedown & Catalog Removal</h2>
          <ul>
            <li>
              Artists wishing to remove a release from the CloudfiMusic catalog must submit
              a written request to <a href="mailto:info@cloudfimusic.com">info@cloudfimusic.com</a> with
              at least <strong>90 days notice</strong>.
            </li>
            <li>
              Early termination during the exclusivity period may result in a buyout fee
              equivalent to the projected remaining revenue, as determined by CloudfiMusic.
            </li>
            <li>
              CloudfiMusic reserves the right to remove any release at any time if it violates
              the <Link to="/music-policy">Music Policy</Link>, is subject to a legal dispute, or
              is deemed harmful to the label’s brand.
            </li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>10. Warranties & Indemnification</h2>
          <ul>
            <li>
              By submitting music for release, the artist warrants that: (a) they are the
              rightful owner or authorized licensee of all submitted material; (b) the material
              does not infringe any third-party rights; (c) they have the authority to enter
              into an agreement with CloudfiMusic.
            </li>
            <li>
              The artist agrees to indemnify and hold harmless CloudfiMusic, Xuân Diệu Media Co., Ltd,
              and its officers, employees, and agents from any claims, damages, or expenses arising
              from a breach of these warranties.
            </li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>11. Governing Law</h2>
          <p>
            This Release Policy and any disputes arising from it shall be governed by and
            construed in accordance with the laws of Vietnam. Any legal proceedings shall
            be conducted in the competent courts of Vietnam.
          </p>
        </section>

        <section className="policy-section">
          <h2>12. Amendments</h2>
          <p>
            CloudfiMusic reserves the right to amend this Release Policy at any time.
            Material changes will be communicated to artists via email or published on
            the CloudfiMusic website. Continued participation in releases following
            notification constitutes acceptance of the revised terms.
          </p>
        </section>

        <section className="policy-section">
          <h2>13. Contact</h2>
          <p>
            For inquiries regarding this Release Policy, please contact:
          </p>
          <p className="policy-contact">
            <strong>CloudfiMusic / Xuân Diệu Media Co., Ltd</strong><br />
            Representative: Xuan Chinh Nguyen<br />
            Email: <a href="mailto:info@cloudfimusic.com">info@cloudfimusic.com</a><br />
            Company ID: 0402222404
          </p>
        </section>

        <div className="policy-footer-nav">
          <Link to="/music-policy">&larr; Music Policy</Link>
          <Link to="/">Back to Home</Link>
        </div>
      </div>
    </div>
  );
}
